import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../auth/AuthContext';
import { PageTitle, Card } from '../ui';
import { toast } from 'sonner';
import { User as UserIcon, Mail, KeyRound, Save, Loader2 } from 'lucide-react';

export default function MonCompte() {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingName, setSavingName] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from('profiles').select('display_name').eq('user_id', user.id).maybeSingle();
      setDisplayName(data?.display_name || '');
      setLoadingProfile(false);
    })();
  }, [user]);

  const saveDisplayName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const name = displayName.trim();
    if (!name) { toast.error('Le nom d\'affichage ne peut pas être vide.'); return; }
    if (name.length > 100) { toast.error('Le nom d\'affichage est trop long (100 caractères max).'); return; }
    setSavingName(true);
    const { error } = await supabase.from('profiles').update({ display_name: name }).eq('user_id', user.id);
    setSavingName(false);
    if (error) toast.error('Impossible de mettre à jour le nom : ' + error.message);
    else toast.success('Nom d\'affichage mis à jour.');
  };

  const changeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) { toast.error('Veuillez saisir une adresse email.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Adresse email invalide.'); return; }
    if (email === user?.email) { toast.error('Cette adresse est déjà votre email actuel.'); return; }
    setSavingEmail(true);
    const { error } = await supabase.auth.updateUser(
      { email },
      { emailRedirectTo: `${window.location.origin}/crm-pro-evenementiel` }
    );
    setSavingEmail(false);
    if (error) {
      const msg = /rate limit/i.test(error.message) ? 'Trop de tentatives, réessayez plus tard.'
        : /already/i.test(error.message) ? 'Cette adresse est déjà utilisée par un autre compte.'
        : 'Impossible de changer l\'email : ' + error.message;
      toast.error(msg);
    } else {
      toast.success('Un email de confirmation a été envoyé à ' + email + '. Cliquez sur le lien pour valider le changement.');
      setNewEmail('');
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwdNew.length < 6) { toast.error('Mot de passe trop court : 6 caractères minimum.'); return; }
    if (pwdNew !== pwdConfirm) { toast.error('Les deux mots de passe ne correspondent pas.'); return; }
    setSavingPwd(true);
    const { error } = await supabase.auth.updateUser({ password: pwdNew });
    setSavingPwd(false);
    if (error) {
      const msg = /should be different/i.test(error.message) ? 'Le nouveau mot de passe doit être différent de l\'actuel.'
        : /weak|short/i.test(error.message) ? 'Mot de passe trop faible ou trop court.'
        : 'Impossible de changer le mot de passe : ' + error.message;
      toast.error(msg);
    } else {
      toast.success('Mot de passe mis à jour avec succès.');
      setPwdNew(''); setPwdConfirm('');
    }
  };

  const inputCls = 'w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50';
  const btnCls = 'inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div>
      <PageTitle title="Mon compte" subtitle="Gérez votre profil, votre email et votre mot de passe." />

      <div className="grid gap-6 max-w-2xl">
        {/* Identité */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserIcon size={16} strokeWidth={1.5} className="text-amber-400" />
            <h2 className="font-heading text-lg text-slate-100">Identité</h2>
          </div>
          <form onSubmit={saveDisplayName} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Email actuel</label>
              <div className="px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-300">{user?.email}</div>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Nom d'affichage</label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={loadingProfile}
                placeholder="Votre nom"
                className={inputCls}
              />
            </div>
            <button type="submit" disabled={savingName || loadingProfile} className={btnCls}>
              {savingName ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.5} />}
              Enregistrer
            </button>
          </form>
        </Card>

        {/* Email */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={16} strokeWidth={1.5} className="text-amber-400" />
            <h2 className="font-heading text-lg text-slate-100">Changer mon email</h2>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Un email de confirmation sera envoyé à la nouvelle adresse. Le changement ne sera effectif qu'après avoir cliqué sur le lien reçu.
          </p>
          <form onSubmit={changeEmail} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Nouvel email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="nouvelle.adresse@exemple.fr"
                className={inputCls}
                autoComplete="email"
              />
            </div>
            <button type="submit" disabled={savingEmail} className={btnCls}>
              {savingEmail ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} strokeWidth={1.5} />}
              Envoyer la confirmation
            </button>
          </form>
        </Card>

        {/* Mot de passe */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} strokeWidth={1.5} className="text-amber-400" />
            <h2 className="font-heading text-lg text-slate-100">Changer mon mot de passe</h2>
          </div>
          <p className="text-xs text-slate-400 mb-3">Minimum 6 caractères.</p>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Nouveau mot de passe</label>
              <input
                type="password"
                value={pwdNew}
                onChange={(e) => setPwdNew(e.target.value)}
                className={inputCls}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                value={pwdConfirm}
                onChange={(e) => setPwdConfirm(e.target.value)}
                className={inputCls}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" disabled={savingPwd} className={btnCls}>
              {savingPwd ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={1.5} />}
              Mettre à jour le mot de passe
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
